import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "./api";
import { unregisterForPush } from "./pushNotifications";
import { getTenantSlug } from "./tenant";
import { rememberSplash } from "./bootSplash";
import { getToken, setSession, clearSession, getRoleForPath, getRealmForRole } from "./authStorage";

const AuthCtx = createContext(null);

// Persona-appropriate login screen to land on after logout — keyed by the
// same roles authStorage.getRoleForPath() resolves from the current path.
const LOGIN_ROUTES = {
  customer: "/login",
  delivery_boy: "/delivery/login",
  tenant_admin: "/admin/login",
  supervisor: "/admin/login",
  super_admin: "/super-admin/login",
  seller: "/seller/login",
};

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guards against showing the "session expired" toast/redirect more than
  // once when several in-flight requests all 401 around the same time
  // (e.g. a page that fires 2-3 parallel API calls right after mount).
  const handlingUnauthorized = useRef(false);

  // A stray API call can 401 (expired/invalid token) from anywhere in the
  // app — lib/api.js's interceptor clears the stored token and dispatches
  // this event so we can bounce to the right login screen with a clear
  // reason, instead of leaving the page stuck on a spinner or a raw error.
  useEffect(() => {
    const onUnauthorized = (e) => {
      if (handlingUnauthorized.current) return;
      handlingUnauthorized.current = true;
      const role = getRoleForPath(window.location.pathname);
      setUser(null);
      // Only a session that actually existed can expire. A fresh install (or
      // a reinstall, or cleared storage, or a logged-out visitor whose screen
      // calls an authenticated endpoint) has no token to begin with, so
      // "your session has expired" is both untrue and alarming — those users
      // just get the login screen. lib/api.js decides which case this is.
      if (e?.detail?.hadSession) {
        toast.error("Your session has expired. Please log in again.");
      }
      navigate(LOGIN_ROUTES[role] || "/login", { replace: true });
      setTimeout(() => { handlingUnauthorized.current = false; }, 1000);
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = getToken();
    const slug = getTenantSlug();
    // Always try to load the tenant by slug so the title/theme/favicon work
    // for guests (logged-out customers) too.
    if (slug) {
      api.get(`/api/public/tenant/${slug}`)
        .then(({ data }) => {
          if (!data?.tenant) return;
          setTenant((cur) => cur || data.tenant);
          // Cache this tenant's splash for the NEXT launch's boot overlay
          // (public/index.html) — or clear it if the tenant has none.
          rememberSplash(slug, data.tenant.branding);
        })
        .catch(() => {});
    }
    if (!token) { setLoading(false); return; }

    // A failure here means one of two very different things, and only one
    // of them means the user is actually logged out:
    //   - the token is genuinely invalid/expired (401) -> clear it.
    //   - a network blip / timeout / 5xx while just checking -> the
    //     session is still perfectly valid, we just couldn't confirm it
    //     right now. Unconditionally clearing on ANY failure here (the
    //     previous behavior) was the root cause of riders — mobile,
    //     frequently on marginal signal — getting bounced to the login
    //     screen on nearly every app launch: a single dropped request
    //     destroyed a valid 30-day session. One short retry covers the
    //     common transient case; NetworkGate's reconnect-triggered
    //     refresh() (App.js) covers a longer outage using the SAME token
    //     this now leaves untouched.
    const checkSession = (retriesLeft) => {
      api.get("/api/auth/me")
        .then(({ data }) => { setUser(data.user); setTenant(data.tenant); setLoading(false); })
        .catch((e) => {
          if (e?.response?.status === 401) {
            clearSession();
            setLoading(false);
          } else if (retriesLeft > 0) {
            setTimeout(() => checkSession(retriesLeft - 1), 1500);
          } else {
            setLoading(false);
          }
        });
    };
    checkSession(1);
  }, []);

  const login = (token, userData, tenantData = null, refreshToken = null) => {
    // Store under the REALM for the role that was just authenticated
    // (getRealmForRole maps supervisor -> the shared tenant_admin bucket,
    // same reasoning as before: nothing ever reads a dedicated
    // "fd_token_supervisor" key). Deliberately NOT derived from
    // window.location.pathname — that was the original design, and it's
    // correct for a fresh login (the user is sitting on that role's own
    // /login page when this fires) but wrong for RoleSwitcher's in-app
    // switch: it calls login() BEFORE navigating to the new role's home
    // page, so the pathname at call time is still the OLD role's page,
    // silently writing the new token into the OLD realm's slot and
    // corrupting that session — the target role from the freshly-issued
    // user object is the only reliable source here.
    setSession(getRealmForRole(userData.role), token, userData, refreshToken);
    setUser(userData);
    if (tenantData) {
      setTenant(tenantData);
    } else {
      // Fetch fresh tenant info so RBAC + module gating works immediately
      api.get("/api/auth/me")
        .then(({ data }) => { setUser(data.user); setTenant(data.tenant); })
        .catch(() => {});
    }
  };

  const logout = async () => {
    // Native (Capacitor) apps embed a tenant-scoped URL (e.g. `/t/<slug>/`)
    // as their WebView start URL — a hard `window.location.href` redirect
    // drops that prefix and dumps the user on the bare origin root, which
    // reads as "kicked out to the web app" instead of staying in-app on a
    // login screen. Navigating in-SPA keeps the tenant context and lands on
    // the persona-appropriate login screen for both native and web.
    const role = getRoleForPath(window.location.pathname);
    // Release this device's push token BEFORE the session is cleared — the
    // unregister endpoint is authenticated, and it deletes only a row
    // belonging to the caller. Leaving the row behind would keep this device
    // bound to the user who just signed out, so the next person to sign in
    // here would receive their notifications.
    // Awaited, but never allowed to block logout.
    try { await unregisterForPush(); } catch { /* logout must always proceed */ }
    clearSession();
    setUser(null);
    const slug = getTenantSlug();
    if (slug) {
      api.get(`/api/public/tenant/${slug}`)
        .then(({ data }) => {
          setTenant(data?.tenant || null);
          if (data?.tenant) rememberSplash(slug, data.tenant.branding);
        })
        .catch(() => setTenant(null));
    } else {
      setTenant(null);
    }
    navigate(LOGIN_ROUTES[role] || "/login", { replace: true });
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data.user); setTenant(data.tenant);
    } catch {}
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
